import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { applySessionReplayActionName, applySessionReplayMask } from 'Services/datadog/datadogRum';
import { isNil, isString } from 'Utilities/helpers';
import proPropTypes from 'Utilities/proPropTypes';

import ProTable from '../ProTable';

import styles from './ListTable.module.scss';

const ListTable = ({
	className,
	hasMultipleTableValues,
	labelClassName,
	mobileVerticalList,
	omitNils,
	proTableParams,
	valueClassName,
	values,
}) => {
	const { className: proTableClassName, ...restProTableParams } = proTableParams;

	const finalClassName = cx(styles.listTable, className, proTableClassName, {
		[styles.mobileVerticalList]: mobileVerticalList,
	});

	return (
		<ProTable className={finalClassName} {...restProTableParams}>
			{values?.map?.(({ className, description, isPII, key, label, value }) => {
				if (omitNils && isNil(value)) {
					return null;
				}

				const labelActionName = `${isString(label) ? label : ''} `;
				const valueActionName = `${labelActionName}value`;

				const additionalValueProps = {
					...(!label && { colSpan: 2 }),
					...(isPII && applySessionReplayMask({ maskedActionName: valueActionName })),
				};

				const TableValueCell = ({ tableValue }) => (
					<td className={cx(styles.value, valueClassName)} {...additionalValueProps}>
						{tableValue}
					</td>
				);

				return (
					<tr key={key} className={className}>
						{label && (
							<td
								className={cx(styles.label, labelClassName)}
								{...(isPII && applySessionReplayActionName(`${labelActionName}label`))}
							>
								{label}
								{description && <span className={styles.description}>{description}</span>}
							</td>
						)}
						{hasMultipleTableValues && Array.isArray(value) ? (
							<>
								{value.map((v) => (
									<TableValueCell key={v} tableValue={v} />
								))}
							</>
						) : (
							<TableValueCell tableValue={value} />
						)}
					</tr>
				);
			})}
		</ProTable>
	);
};

ListTable.defaultProps = {
	className: null,
	hasMultipleTableValues: false,
	labelClassName: null,
	mobileVerticalList: false,
	omitNils: false,
	proTableParams: {},
	valueClassName: null,
	values: [],
};

const valuesElementPropType = PropTypes.oneOfType([
	PropTypes.string,
	PropTypes.node,
	PropTypes.number,
	PropTypes.object,
	PropTypes.bool,
]);

export const valuesPropType = PropTypes.arrayOf(
	PropTypes.shape({
		className: proPropTypes.className,
		description: valuesElementPropType,
		key: PropTypes.string.isRequired,
		label: valuesElementPropType,
		value: valuesElementPropType,
	}),
);

ListTable.propTypes = {
	className: proPropTypes.className,
	hasMultipleTableValues: PropTypes.bool,
	labelClassName: proPropTypes.className,
	mobileVerticalList: PropTypes.bool,
	omitNils: PropTypes.bool,
	proTableParams: PropTypes.shape({
		alternateRowHighlight: PropTypes.oneOf(['odd', 'even', 'none']),
		className: proPropTypes.className,
		containerClassName: proPropTypes.className,
		edgeToEdgeOnMobile: PropTypes.bool,
		tableProps: proPropTypes.tableProps,
	}),
	valueClassName: proPropTypes.className,
	values: valuesPropType,
};

export default ListTable;
